import type { ReactElement } from 'react';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiVolopPromotionRadius } from '#/modules/listing/volops/api/types/ApiVolopPromotionRadius';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import { SearchEventHit } from '#/modules/search/components/EventHit/SearchEventHit';
import { SearchImportedHit } from '#/modules/search/components/ImportedHit/SearchImportedHit';
import { SearchInternshipHit } from '#/modules/search/components/InternshipHit/SearchInternshipHit';
import { SearchJobHit } from '#/modules/search/components/JobHit/SearchJobHit';
import { SearchOrgHit } from '#/modules/search/components/OrgHit/SearchOrgHit';
import { SearchVolopHit } from '#/modules/search/components/VolopHit/SearchVolopHit';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';

type Props = {
  hit: SearchAlgoliaHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  /**
   * Providing `null` hides the save button
   */
  searchSavedListingsStore: SearchSavedListingsStore | null;
  hideOwnerBanner: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions: ReactElement | null;
  promotionRadius: ApiVolopPromotionRadius | null;
};

export function SearchHit({
  hit,
  searchSavedListingsStore,
  hideOwnerBanner,
  highlighted,
  application,
  actions,
  trackClick,
  showLabel,
  promotionRadius,
}: Props) {
  const { locale } = useI18n();

  switch (hit.type) {
    case 'JOB':
      return (
        <SearchJobHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'INTERNSHIP':
      return (
        <SearchInternshipHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'VOLOP':
      return (
        <SearchVolopHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
          promotionRadius={promotionRadius}
        />
      );
    case 'EVENT':
      return (
        <SearchEventHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          application={application}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'IMPORTED':
      return (
        <SearchImportedHit
          hit={hit}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'ORG':
      return (
        <SearchOrgHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          hideOwnerBanner={hideOwnerBanner}
          highlighted={highlighted}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
  }
}
